import jsPDF from 'jspdf';
import { waitForReportChartVisuals, captureDashboardForPdf } from './reportChartReady';

/** Límite SES: mensaje raw 10 MB; PDF adjunto ~7.5 MB (base64 + headers). */
export const SES_MAX_PDF_BYTES = 7_500_000;

export function buildReportFileName(reportName) {
  return `${String(reportName || 'reporte').replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
}

export function pdfOutputToBase64(pdf) {
  const dataUri = pdf.output('datauristring');
  return dataUri.includes(',') ? dataUri.split(',')[1] : '';
}

export function estimatePdfBytesFromBase64(base64) {
  if (!base64) return 0;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

async function loadLogoImage(reportLogoPath) {
  const logoImg = new Image();
  logoImg.crossOrigin = 'anonymous';
  await new Promise((resolve) => {
    logoImg.onload = resolve;
    logoImg.onerror = () => {
      console.warn('No se pudo cargar el logo del reporte');
      resolve();
    };
    logoImg.src = reportLogoPath;
    setTimeout(() => {
      if (!logoImg.complete) resolve();
    }, 3000);
  });
  return logoImg.complete && logoImg.naturalWidth > 0 ? logoImg : null;
}

/** Zona fija del logo en la cabecera. Cualquier forma entra dentro, sin recortar ni estirar. */
const LOGO_SLOT_W_MM = 50;
const LOGO_SLOT_H_MM = 20;
const LOGO_SLOT_W_PX = 400;
const LOGO_SLOT_H_PX = 160;

function containSize(srcW, srcH, maxW, maxH) {
  if (!(srcW > 0) || !(srcH > 0) || !(maxW > 0) || !(maxH > 0)) return null;
  const scale = Math.min(maxW / srcW, maxH / srcH);
  return { width: srcW * scale, height: srcH * scale };
}

/** Reescala el archivo a la zona del logo. Así un PNG enorme no se inserta a tamaño de píxel. */
function rasterizeLogoContained(img) {
  const px = containSize(img.naturalWidth, img.naturalHeight, LOGO_SLOT_W_PX, LOGO_SLOT_H_PX);
  const mm = containSize(img.naturalWidth, img.naturalHeight, LOGO_SLOT_W_MM, LOGO_SLOT_H_MM);
  if (!px || !mm) return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(px.width));
  canvas.height = Math.max(1, Math.round(px.height));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return {
    dataUrl: canvas.toDataURL('image/png'),
    widthMm: mm.width,
    heightMm: mm.height,
  };
}

/** Logo + fecha en la primera fila; título del reporte debajo, a ancho de página, con wrap.
 * Así un nombre largo (reporte + dashboard) no pisa la fecha ni sale a 18pt en una sola línea. */
function drawReportPdfHeader(pdf, {
  reportName,
  drawnLogo,
  margin,
  pageWidth,
  generatedAtLabel,
  brandColor,
  secondaryTextColor,
  ruleColor,
}) {
  const topY = margin;
  if (drawnLogo) {
    const logoY = topY + (LOGO_SLOT_H_MM - drawnLogo.heightMm) / 2;
    pdf.addImage(drawnLogo.dataUrl, 'PNG', margin, logoY, drawnLogo.widthMm, drawnLogo.heightMm);
  }
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...secondaryTextColor);
  const dateY = topY + (drawnLogo ? 6 : 4);
  pdf.text(generatedAtLabel, pageWidth - margin, dateY, { align: 'right' });

  let y = topY + (drawnLogo ? LOGO_SLOT_H_MM : 8) + 5;
  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(...brandColor);
  const lines = pdf.splitTextToSize(String(reportName || 'Reporte'), pageWidth - 2 * margin);
  pdf.text(lines, margin, y);
  y += lines.length * 5.5 + 3;
  pdf.setDrawColor(...ruleColor);
  pdf.setLineWidth(0.4);
  pdf.line(margin, y, pageWidth - margin, y);
  pdf.setTextColor(0, 0, 0);
  return y + 8;
}

function canvasToImage(canvas, { useJpeg, jpegQuality }) {
  if (useJpeg) {
    return {
      data: canvas.toDataURL('image/jpeg', jpegQuality),
      format: 'JPEG',
    };
  }
  return {
    data: canvas.toDataURL('image/png'),
    format: 'PNG',
  };
}

/**
 * Inserta la captura a ancho completo y la parte en páginas si no cabe en una.
 * Evita el recorte/encogido que dejaba el dashboard cortado en el PDF.
 * @returns {number} Y (mm) tras la última franja dibujada
 */
function addCanvasPaginated(pdf, canvas, {
  margin,
  pageHeight,
  contentWidth,
  startY,
  useJpeg,
  jpegQuality,
}) {
  const imgWidth = contentWidth;
  const fullImgHeight = (canvas.height * imgWidth) / canvas.width;
  if (!(canvas.width > 0) || !(canvas.height > 0) || !(fullImgHeight > 0)) {
    return startY;
  }

  const pxPerMm = canvas.height / fullImgHeight;
  let y = startY;
  let srcYPx = 0;
  let remainingMm = fullImgHeight;
  const minSliceMm = 12;

  while (remainingMm > 0.4 && srcYPx < canvas.height - 0.5) {
    let available = pageHeight - margin - y;
    if (available < minSliceMm) {
      pdf.addPage();
      y = margin;
      available = pageHeight - margin - y;
    }

    const sliceMm = Math.min(remainingMm, available);
    let slicePx = Math.max(1, Math.round(sliceMm * pxPerMm));
    if (srcYPx + slicePx > canvas.height) {
      slicePx = canvas.height - srcYPx;
    }
    if (slicePx <= 0) break;

    const actualSliceMm = (slicePx / canvas.height) * fullImgHeight;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = slicePx;
    const ctx = sliceCanvas.getContext('2d');
    if (useJpeg) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    }
    ctx.drawImage(
      canvas,
      0,
      srcYPx,
      canvas.width,
      slicePx,
      0,
      0,
      canvas.width,
      slicePx
    );

    const { data, format } = canvasToImage(sliceCanvas, { useJpeg, jpegQuality });
    pdf.addImage(data, format, margin, y, imgWidth, actualSliceMm);

    srcYPx += slicePx;
    remainingMm -= actualSliceMm;
    y += actualSliceMm;

    if (remainingMm > 0.4 && srcYPx < canvas.height - 0.5) {
      pdf.addPage();
      y = margin;
    }
  }

  return y;
}

function scaleCanvas(canvas, factor) {
  if (!canvas || factor >= 0.999) return canvas;
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(canvas.width * factor));
  out.height = Math.max(1, Math.round(canvas.height * factor));
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0, out.width, out.height);
  return out;
}

async function captureDashboardCanvases(dashboardCaptures, html2canvas, scale) {
  const canvases = [];
  for (const capture of dashboardCaptures) {
    const dashboardElement = typeof capture.getElement === 'function' ? capture.getElement() : null;
    if (!dashboardElement) {
      canvases.push(null);
      continue;
    }
    await waitForReportChartVisuals(dashboardElement, { timeoutMs: 20000 });
    await new Promise((resolve) => setTimeout(resolve, 300));
    canvases.push(await captureDashboardForPdf(dashboardElement, html2canvas, { scale }));
  }
  return canvases;
}

async function renderReportPdf({
  report,
  reportLogoPath,
  dashboardCaptures,
  canvases = null,
  html2canvas,
  jsPDFClass,
  scale,
  useJpeg,
  jpegQuality,
}) {
  const pdf = new jsPDFClass('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  // Mismos tonos que vsaasTheme (client/src/theme.js): primary.main y text.secondary.
  const BRAND_COLOR = [94, 110, 180];
  const SECONDARY_TEXT_COLOR = [91, 113, 121];
  const RULE_COLOR = [227, 235, 238]; // vsaasTheme.border.light

  const generatedAt = new Date();
  const generatedAtLabel = `Generado el: ${generatedAt.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })}, ${generatedAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;

  let currentY;
  try {
    const logoImg = await loadLogoImage(reportLogoPath);
    const drawnLogo = logoImg ? rasterizeLogoContained(logoImg) : null;
    currentY = drawReportPdfHeader(pdf, {
      reportName: report.name,
      drawnLogo,
      margin,
      pageWidth,
      generatedAtLabel,
      brandColor: BRAND_COLOR,
      secondaryTextColor: SECONDARY_TEXT_COLOR,
      ruleColor: RULE_COLOR,
    });
  } catch (err) {
    console.error('Error al cargar logo en PDF:', err);
    currentY = drawReportPdfHeader(pdf, {
      reportName: report.name,
      drawnLogo: null,
      margin,
      pageWidth,
      generatedAtLabel,
      brandColor: BRAND_COLOR,
      secondaryTextColor: SECONDARY_TEXT_COLOR,
      ruleColor: RULE_COLOR,
    });
  }

  for (let i = 0; i < dashboardCaptures.length; i++) {
    const { name, getElement } = dashboardCaptures[i];
    const dashboardElement = typeof getElement === 'function' ? getElement() : null;

    let canvas = canvases ? canvases[i] : null;
    if (canvas || dashboardElement) {
      try {
        if (!canvas && dashboardElement) {
          await waitForReportChartVisuals(dashboardElement, { timeoutMs: 20000 });
          await new Promise((resolve) => setTimeout(resolve, 300));
          canvas = await captureDashboardForPdf(dashboardElement, html2canvas, { scale });
        }
        if (!canvas) throw new Error('Sin captura');
        const showSectionTitle = dashboardCaptures.length > 1;
        const titleHeight = showSectionTitle ? 10 : 0;
        const minSpaceForTitle = titleHeight + 20;
        if (currentY + minSpaceForTitle > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }

        if (showSectionTitle) {
          pdf.setFont(undefined, 'bold');
          pdf.setFontSize(14);
          pdf.setTextColor(...SECONDARY_TEXT_COLOR);
          pdf.text(`${i + 1}. ${name || 'Dashboard sin nombre'}`, margin, currentY);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(0, 0, 0);
          currentY += titleHeight;
        }
        currentY = addCanvasPaginated(pdf, canvas, {
          margin,
          pageHeight,
          contentWidth,
          startY: currentY,
          useJpeg,
          jpegQuality,
        });
        currentY += 10;
      } catch (err) {
        console.error('Error al capturar dashboard para PDF:', err);
        if (currentY > pageHeight - 20) {
          pdf.addPage();
          currentY = margin;
        }
        pdf.setFontSize(10);
        pdf.text('Error al generar visualización de este dashboard', margin, currentY);
        currentY += 10;
      }
    } else {
      if (currentY > pageHeight - 60) {
        pdf.addPage();
        currentY = margin;
      }
      pdf.setFontSize(10);
      pdf.text('Dashboard no disponible', margin, currentY);
      currentY += 10;
    }

    if (i < dashboardCaptures.length - 1) {
      if (currentY > pageHeight - 20) {
        pdf.addPage();
        currentY = margin;
      }
      currentY += 5;
    }
  }

  const fileName = buildReportFileName(report.name);
  const base64 = pdfOutputToBase64(pdf);
  return { pdf, fileName, base64, bytes: estimatePdfBytesFromBase64(base64) };
}

/**
 * Genera el PDF del reporte (misma lógica que el botón "Descargar PDF").
 * compressForEmail: reintenta con JPEG y menor escala para caber en SES (10 MB mensaje).
 */
export async function buildReportPdf({
  report,
  reportLogoPath,
  dashboardCaptures,
  html2canvas,
  jsPDFClass = jsPDF,
  compressForEmail = false,
}) {
  if (!report) throw new Error('Reporte no definido');
  if (!dashboardCaptures?.length) throw new Error('No hay dashboards para generar el PDF');

  if (!compressForEmail) {
    const result = await renderReportPdf({
      report,
      reportLogoPath,
      dashboardCaptures,
      html2canvas,
      jsPDFClass,
      scale: 1.5,
      useJpeg: false,
      jpegQuality: 0.95,
    });
    return { ...result, compressionAttempt: { scale: 1.5, useJpeg: false, jpegQuality: 0.95 } };
  }

  // Una sola captura a escala 1.5. Si no cabe en SES, se reescala ese canvas y se
  // reencoda en JPEG: volver a capturar el DOM no achica el plano del heatmap.
  const CAPTURE_SCALE = 1.5;
  const captured = await captureDashboardCanvases(dashboardCaptures, html2canvas, CAPTURE_SCALE);
  // Misma captura a escala 1.5. Solo baja la calidad JPEG; no se achica la imagen.
  const attempts = [
    { factor: 1, jpegQuality: 0.97 },
    { factor: 1, jpegQuality: 0.9 },
    { factor: 1, jpegQuality: 0.85 },
  ];

  let lastResult = null;
  let lastAttempt = attempts[0];
  for (const attempt of attempts) {
    lastAttempt = attempt;
    lastResult = await renderReportPdf({
      report,
      reportLogoPath,
      dashboardCaptures,
      canvases: captured.map((canvas) => scaleCanvas(canvas, attempt.factor)),
      html2canvas,
      jsPDFClass,
      scale: CAPTURE_SCALE,
      useJpeg: true,
      jpegQuality: attempt.jpegQuality,
    });
    const compressionAttempt = {
      scale: Math.round(CAPTURE_SCALE * attempt.factor * 100) / 100,
      useJpeg: true,
      jpegQuality: attempt.jpegQuality,
    };
    if (lastResult.bytes <= SES_MAX_PDF_BYTES) {
      return { ...lastResult, compressionAttempt };
    }
  }

  if (lastResult?.bytes > SES_MAX_PDF_BYTES) {
    const scale = Math.round(CAPTURE_SCALE * lastAttempt.factor * 100) / 100;
    throw new Error(
      `El PDF (${Math.round(lastResult.bytes / 1024 / 1024)} MB) supera el límite de correo (${Math.round(SES_MAX_PDF_BYTES / 1024 / 1024)} MB) ` +
      `después de comprimir (escala ${scale}, JPEG ${Math.round(lastAttempt.jpegQuality * 100)}%). ` +
      'Reduce dashboards o gráficos en el reporte.'
    );
  }
  return lastResult;
}
